import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PLATFORMS, PLATFORM_LABELS } from "@/lib/helpers";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const CATEGORIES = ["Food", "Fashion", "Electronics", "Beauty", "Home", "Industrial", "Other"];

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [platform, setPlatform] = useState("amazon");
  const [submitting, setSubmitting] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const finish = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/complete-onboarding", { category, platform });
      await refresh();
      toast.success("Setup complete");
      nav("/dashboard");
    } catch { toast.error("Failed to finish onboarding"); }
    finally { setSubmitting(false); }
  };

  const progress = (step / 3) * 100;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="logo-mark">EK</div>
            <span className="font-display font-semibold text-navy">EcomKit</span>
          </div>
          <div className="text-xs font-mono-data text-muted-foreground">Setup · {Math.round(progress)}%</div>
        </div>
        <div className="h-1 bg-border">
          <div className="h-full bg-orange transition-all" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          {step === 1 && (
            <div>
              <div className="eyebrow mb-3">Step 1 of 3 · Welcome</div>
              <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tighter text-navy">What do you sell online?</h1>
              <p className="mt-3 text-muted-foreground">Pick the category that best describes your catalog. This helps EcomKit tune AI suggestions for you.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                {CATEGORIES.map((c) => (
                  <button key={c} data-testid={`cat-${c.toLowerCase()}`} onClick={() => setCategory(c)}
                    className={`p-5 text-left rounded-lg border-2 font-medium text-sm transition-colors ${
                      category === c ? "border-orange bg-orange/5 text-navy" : "border-border bg-white text-muted-foreground hover:border-navy/40"
                    }`}>{c}</button>
                ))}
              </div>
              <div className="mt-10">
                <button data-testid="step1-next" disabled={!category} onClick={() => setStep(2)} className="btn-primary">
                  Continue <ArrowRight size={14} weight="bold"/>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="eyebrow mb-3">Step 2 of 3 · First platform</div>
              <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tighter text-navy">Connect your first marketplace</h1>
              <p className="mt-3 text-muted-foreground">Your first marketplace is <span className="text-orange font-semibold">free forever</span>. Additional platforms are ₹500/day, configurable in admin.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                {PLATFORMS.map((p) => (
                  <button key={p} data-testid={`plat-${p}`} onClick={() => setPlatform(p)}
                    className={`p-6 text-center rounded-lg border-2 transition-colors ${
                      platform === p ? "border-orange bg-orange/5" : "border-border bg-white hover:border-navy/40"
                    }`}>
                    <div className="font-display font-semibold text-lg text-navy">{PLATFORM_LABELS[p]}</div>
                    <div className="text-[10px] font-mono-data text-muted-foreground mt-2 tracking-widest">DEMO ADAPTER</div>
                  </button>
                ))}
              </div>
              <div className="mt-10 flex gap-3">
                <button onClick={() => setStep(1)} className="btn-secondary">← Back</button>
                <button data-testid="step2-next" onClick={() => setStep(3)} className="btn-primary">
                  Continue <ArrowRight size={14} weight="bold"/>
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="eyebrow mb-3">Step 3 of 3 · Almost there</div>
              <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tighter text-navy">We'll seed 10 demo products for you</h1>
              <p className="mt-3 text-muted-foreground">You can delete them anytime or import your own catalog from CSV/XLSX.</p>

              <div className="card-base p-6 mt-8 grid grid-cols-2 gap-6">
                <div>
                  <div className="eyebrow mb-1">Category</div>
                  <div className="font-display text-xl font-semibold text-navy">{category}</div>
                </div>
                <div>
                  <div className="eyebrow mb-1">First platform</div>
                  <div className="font-display text-xl font-semibold text-navy flex items-center gap-2">
                    {PLATFORM_LABELS[platform]} <span className="pill pill-live">Demo</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                {["10 demo products seeded", "Availability matrix ready", "AI Builder unlocked", "Product gap detection live"].map((t)=>(
                  <div key={t} className="flex items-center gap-2 text-sm text-navy"><CheckCircle size={16} weight="fill" className="text-brand-green"/> {t}</div>
                ))}
              </div>

              <div className="mt-10 flex gap-3">
                <button onClick={() => setStep(2)} className="btn-secondary">← Back</button>
                <button data-testid="step3-finish" disabled={submitting} onClick={finish} className="btn-primary">
                  {submitting ? "Setting up…" : <>Enter EcomKit <ArrowRight size={14} weight="bold"/></>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
